













































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import {
  useCreateBankPaymentInstructionProperty,
  useUpdateBankPaymentInstructionProperty
} from '@/composable/bank';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IBankPaymentInstructionProperty,
  IBankPaymentInstructionPropertyData,
  BankPaymentInstructionProperty
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';

export default defineComponent({
  name: 'PaymentInstructionPropertyDialog',
  components: {
    ALDialog,
    ErrorAlert
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    paymentInstructionProperty: {
      type: Object as PropType<IBankPaymentInstructionPropertyData | null>,
      default: null
    },
    instructionId: Number,
    instructionIdentifier: String
  },
  emits: ['update:dialog'],
  setup(props, { emit, root }) {
    const computedInstructionId = computed(() => props.instructionId!);

    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const paymentInstructionPropertyData = ref<
      IBankPaymentInstructionPropertyData
    >({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        paymentInstructionPropertyData.value = !props.isEdit
          ? ({} as IBankPaymentInstructionPropertyData)
          : cloneDeep(props.paymentInstructionProperty!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () =>
        `${
          props.isEdit ? 'Update' : 'Create'
        } payment instruction property information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updatePaymentInstructionProperty,
      reset: updateReset
    } = useUpdateBankPaymentInstructionProperty(computedInstructionId);
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createPaymentInstructionProperty,
      reset: createReset
    } = useCreateBankPaymentInstructionProperty(computedInstructionId);

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let paymentInstructionPropertyInstance: IBankPaymentInstructionProperty | null = null;

        try {
          paymentInstructionPropertyInstance = new BankPaymentInstructionProperty(
            paymentInstructionPropertyData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (paymentInstructionPropertyInstance != null) {
          const saveAction = props.isEdit
            ? updatePaymentInstructionProperty
            : createPaymentInstructionProperty;

          let payload = paymentInstructionPropertyInstance;
          if (props.isEdit) {
            payload = diffDeep(
              paymentInstructionPropertyInstance,
              props.paymentInstructionProperty,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      paymentInstructionPropertyData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error
    };
  }
});
